import React, { useState, useMemo } from "react";
import { OvalNeumorphicBtn } from "../buttons";
import { XIcon, LeftArrowIcon, WhistleIcon } from "../icons";
import { getReportFlow } from "../../utils/reportFlow";
import "./reportModal.scss";


export default function ReportModal({ kind, onBlowWhistle, onCancel }) {
  const steps = useMemo(() => getReportFlow(kind), [kind]);
  const [history, setHistory] = useState(["why"]);
  const [selected, setSelected] = useState([]);
  const [inputValue, setInputValue] = useState("");

  // Build a lookup map from optionId → label
   const id2label = useMemo(() => {
       const m = {};
       steps.forEach((step) => {
         if (step.options) {
           step.options.forEach((opt) => {
             m[opt.id] = opt.label;
           });
         }
       });
       return m;
     }, [steps]);
    
  const currentId = history[history.length - 1];
  const step = steps.find((s) => s.id === currentId);

  const goBack = () => {
    if (history.length === 1) return onCancel();
    setHistory((h) => h.slice(0, -1));
    setSelected((s) => s.slice(0, -1));
    setInputValue("");
  };

  const advance = (answer) => {
    setSelected((s) => [...s, answer]);
    const nextStep = step.next ?? answer.next;
    if (nextStep) {
      setHistory((h) => [...h, nextStep]);
    } else {
      onBlowWhistle([...selected, answer]);
    }
    setInputValue("");
  };

  return (
    <div className="report-modal-overlay">
      <div className="report-modal">
        {/* -------- header -------- */}
        <header className="report-modal-header">
          <div className="header-btns">
            {history.length > 1 && (
              <OvalNeumorphicBtn
                onClick={goBack}
                icon={
                  <LeftArrowIcon
                    width="15"
                    height="15"
                    fillColor="var(--color-gold)"
                  />
                }
                text="Back"
                fontSize="18px"
                fontFamily="Roboto, sans-serif"
                fontWeight="900"
              />
            )}
            <OvalNeumorphicBtn
              onClick={onCancel}
              icon={
                <XIcon width="15" height="15" fillColor="var(--color-gold)" />
              }
              text="Cancel"
              fontSize="18px"
              fontFamily="Roboto, sans-serif"
              fontWeight="900"
            />
          </div>
          <h3 className="report-modal-title">{step.title}</h3>
        </header>

        {/* -------- subtitle -------- */}
        {step.subtitle && (
          <p className="report-modal-subtitle">{step.subtitle}</p>
        )}

        {/* -------- list mode -------- */}
        {step.type === "list" && (
          <ul className="report-options">
            {step.options.map((opt) => (
              <li
                key={opt.id}
                className="report-option"
                onClick={() =>
                  advance({ stepId: step.id, optionId: opt.id, next: opt.next })
                }
              >
                <span>{opt.label}</span>
                {opt.next && <span className="chevron">›</span>}
              </li>
            ))}
          </ul>
        )}

        {/* -------- input mode -------- */}
        {step.type === "input" && (
          <div className="report-input-step" style={{ padding: "12px" }}>
            <textarea
              rows={3}
              className="report-input-textarea"
              placeholder="Describe the issue…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <OvalNeumorphicBtn
              onClick={() =>
                advance({ stepId: step.id, text: inputValue.trim() })
              }
              text="Next"
              disabled={!inputValue.trim()}
              fontSize="18px"
              fontFamily="Roboto, sans-serif"
              fontWeight="bold"
              style={{ width: "100%", marginTop: "12px" }}
            />
          </div>
        )}

        {/* -------- summary mode -------- */}
        {step.type === "summary" && (
          <div className="report-summary" style={{ padding: "12px" }}>
            {selected.map((ans, i) => (
              <div key={i} className="report-summary-row">
                <strong>
                  {steps.find((f) => f.id === ans.stepId).title.replace("?", "")}
                </strong>
                :{" "}
                {
                  ans.text
                    ? ans.text
                    : id2label[ans.optionId] /* ← human-readable label */
                }
              </div>
            ))}
            <OvalNeumorphicBtn
              onClick={() => onBlowWhistle(selected)}
              icon={
                <WhistleIcon
                  width="20"
                  height="20"
                  style={{ marginRight: "8px" }}
                />
              }
              color="var(--color-bloodred)"
              text="Submit report"
              fontSize="18px"
              fontFamily="Roboto, sans-serif"
              fontWeight="900"
              style={{ width: "100%", marginTop: "12px" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
